import React, { useEffect, useState, useRef } from "react";
import CgFormCreator from "../../../common/CgFormCreator";
import AddedValuesList from "../../../common/AddedValuesList";
import PopupModal from "../../../common/PopupModal";
import "./providerSpeciality.scss";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  fetchSpecialityList,
  addProviderSpeciality,
  updateProviderSpeciality,
  fetchSpecialityLanguageSuggestions,
  fetchSpecialtyOptions,
  fetchDeleteSpeciality,
} from "../../../store/ProviderInfo/ProviderSpecialityActions";
import redAlert from "../../../assets/images/redAlert.svg";

const GENERAL_DENTISTRY_LABEL = "General Dentistry";

const getInstitutionName = (specialty) =>
  specialty?.institutionName?.trim?.() ||
  specialty?.instituteName?.trim?.() ||
  "";

const normalizeSpecialty = (specialty) => {
  if (!specialty) {
    return specialty;
  }

  const institutionName = getInstitutionName(specialty);

  return {
    ...specialty,
    institutionName: institutionName || null,
    instituteName: specialty.instituteName ?? institutionName,
  };
};

const normalizeSpecialties = (specialties) =>
  (specialties || []).map(normalizeSpecialty);

const toApiSpecialty = (item) => ({
  specialtyName: item.specialtyName,
  institutionName: getInstitutionName(item),
  year: parseInt(item.year, 10) || 0,
  isValid: item.isValid === "true" || item.isValid === true,
});

const isInstituteMissingInSpecialty = (specialty) => {
  if (specialty.specialtyName === GENERAL_DENTISTRY_LABEL) {
    return false;
  }

  return !getInstitutionName(specialty);
};

const ProviderSpeciality = ({
  onlyPopup = false,
  closePopupCallback = () => {},
  openSpecialityPopup,
  providerId: parentProviderId,
}) => {
  const location = useLocation();
  const locationData = location.pathname.split("/");
  const providerId = parentProviderId ?? locationData[2];
  console.debug({ providerId });
  const [openPopup, setOpenPopup] = useState(false);
  const [popupState, setPopupState] = useState("");
  const [addedSpecialityValues, setAddedSpecialityValues] = useState([]);
  const [editId, setEditId] = useState();
  const [confirmDeleteMap, setConfirmDeleteMap] = useState({});
  const [missingData, setMissingData] = useState(false);
  const [renderErrorMsg, setRenderErrorMsg] = useState("");
  const [resetValues, setResetValues] = useState(false);
  const [processedSpecialties, setProcessedSpecialties] = useState([]);
  const [showError, setShowError] = useState(false);
  const addedInstitutionRef = useRef([]);
  const [lastUpdatedId, setLastUpdatedId] = useState(null);
  const [prvId, setPrvId] = useState(providerId);
  const [enableAddButton, setEnableAddButton] = useState(false);
  const [enableTrainingInstitution, setEnableTrainingInstitution] =
    useState(false);
  const [selectedSpecialty, setSelectedSpecialty] = useState("");
  const [selectedInstitution, setSelectedInstitution] = useState("");
  const dispatch = useDispatch();
  const {
    specialityData,
    isSpecialityLoading,
    specialityError,
    specialityErrorMsg,
    addSpecialityError,
    addSpecialityErrorMsg,
    deleteSpecialityError,
    deleteSpecialityErrorMsg,
    updateSpecialityError,
    updateSpecialityErrorMsg,
    specialtyOptions,
    directDeleteError,
    directDeleteErrorMsg,
  } = useSelector((state) => state.providerSpeciality);

  const formattedSpecialtyOptions = React.useMemo(() => {
    const defaultOption = [];

    if (!specialtyOptions || !Array.isArray(specialtyOptions)) {
      return defaultOption;
    }

    return [
      ...defaultOption,
      ...specialtyOptions.map((specialty, index) => ({
        id: index + 1,
        value: specialty.name,
      })),
    ];
  }, [specialtyOptions]);

  const { suggestionData } = useSelector(
    (state) => state.SpecialitylangSuggestion
  );

  const sortSpecialties = (specialties, updatedId = null) => {
    const idToUse = updatedId || lastUpdatedId;

    if (idToUse) {
      return [...specialties].sort((a, b) => {
        if (a.displayId === idToUse) return -1;
        if (b.displayId === idToUse) return 1;
        return a.specialtyName.localeCompare(b.specialtyName);
      });
    }

    return [...specialties].sort((a, b) =>
      a.specialtyName.localeCompare(b.specialtyName)
    );
  };

  useEffect(() => {
    const shouldEnable =
      (selectedSpecialty && selectedInstitution) ||
      selectedSpecialty === GENERAL_DENTISTRY_LABEL;
    setEnableAddButton(shouldEnable);
  }, [selectedSpecialty, selectedInstitution]);

  useEffect(() => {
    if (providerId) {
      setPrvId(providerId);
    } else {
      setPrvId(providerId);
    }
  }, [providerId]);

  useEffect(() => {
    dispatch(fetchSpecialtyOptions(prvId));
  }, []);

  useEffect(() => {
    setOpenPopup(openSpecialityPopup);
    setPopupState("add");
    if (openSpecialityPopup) {
      setEnableAddButton(false);
      setEnableTrainingInstitution(false);
      setSelectedSpecialty("");
      setSelectedInstitution("");
    }
  }, [openSpecialityPopup]);

  useEffect(() => {
    if (specialityData) {
      let processedData = [];
      if (
        specialityData.data &&
        specialityData.data.providerSpecialtiesResponseDTO
      ) {
        processedData = specialityData.data.providerSpecialtiesResponseDTO;
      } else if (specialityData.providerSpecialtiesResponseDTO) {
        processedData = specialityData.providerSpecialtiesResponseDTO;
      } else if (Array.isArray(specialityData)) {
        processedData = specialityData;
      }

      if (Array.isArray(processedData)) {
        const validSpecialties = processedData.filter(
          (item) => item.isValid === "true" || item.isValid === true
        );
        const uniqueSpecialties = [];
        const specialtyNames = new Set();

        validSpecialties.forEach((specialty) => {
          if (!specialtyNames.has(specialty.specialtyName)) {
            specialtyNames.add(specialty.specialtyName);
            uniqueSpecialties.push(specialty);
          }
        });

        const sortedSpecialties = sortSpecialties(
          normalizeSpecialties(uniqueSpecialties)
        );

        setProcessedSpecialties(sortedSpecialties);
        setAddedSpecialityValues(sortedSpecialties);
        handleMissingData(sortedSpecialties);
      } else {
        setProcessedSpecialties([]);
        setAddedSpecialityValues([]);
      }
    }
  }, [specialityData, lastUpdatedId]);

  useEffect(() => {
    if (prvId) {
      dispatch(fetchSpecialityList(prvId));
    }
  }, [prvId]);

  useEffect(() => {
    if (
      addSpecialityError ||
      updateSpecialityError ||
      deleteSpecialityError ||
      directDeleteError
    ) {
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 5000);
    }
  }, [
    addSpecialityError,
    updateSpecialityError,
    deleteSpecialityError,
    directDeleteError,
  ]);

  useEffect(() => {
    if (
      specialityError &&
      specialityData &&
      (!processedSpecialties || processedSpecialties.length === 0)
    ) {
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 5000);
    }
  }, [specialityData, processedSpecialties, specialityError]);

  const trainingCompletedDropdownOptions = React.useMemo(() => {
    const options = [{ id: 0, value: "Select year" }];
    const currentYear = 2025;
    const endYear = 1990;
    for (let year = currentYear, id = 1; year >= endYear; year--, id++) {
      options.push({ id, value: year.toString() });
    }
    return options;
  }, []);

  const AddSpecialitySchema = [
    {
      component: "dropdown",
      label: "Specialty",
      name: "specialtyName",
      dropdownPlaceHolder: "Select a specialty",
      defaultDropdownPlaceHolder: " a speciality",
      defaultValue: popupState !== "add" && editId?.specialtyName,
      dropdownOptions: formattedSpecialtyOptions,
      Placeholder: "Select a speciality",
      onSelectCallBack: (name, value) => {
        setSelectedSpecialty(value || "");
        if (value && value !== GENERAL_DENTISTRY_LABEL) {
          setEnableTrainingInstitution(true);
        } else {
          setEnableTrainingInstitution(false);
          setSelectedInstitution("");
        }
      },
      required: false,
    },
    {
      component: "autoComplete",
      id: "instituteName",
      label: "Training Institution",
      type: "text",
      name: "instituteName",
      autoCompletePlaceHolders: "Enter Institution",
      required: false,
      disabled: popupState === "add" ? !enableTrainingInstitution : false,
      defaultValue: popupState !== "add" ? getInstitutionName(editId) : "",
      suggestions: suggestionData || [],
      renderAddedValues: false,
      isMultiple: false,
      noErrorInAutoComplate: true,
      isEdit: true,
      resetValue: !openPopup,
      onValueSelect: (name, values) => {
        addedInstitutionRef.current = values;
        setSelectedInstitution(values || "");
      },
      onValueChangeCallback: (res) => {
        dispatch(fetchSpecialityLanguageSuggestions(res));
        if (res && res.trim() !== "") {
          setSelectedInstitution(res);
        }
      },
    },
    {
      component: "dropdown",
      label: "Training Completed",
      defaultDropdownPlaceHolder: " year",
      name: "year",
      defaultValue: popupState !== "add" ? editId?.year : "Select year",
      dropdownOptions: trainingCompletedDropdownOptions,
      onSelectCallBack: () => {},
      required: false,
      disabled: selectedSpecialty === GENERAL_DENTISTRY_LABEL,
    },
  ];

  const handleMissingData = (data = processedSpecialties) => {
    const hasMissingData = data.some((item) =>
      isInstituteMissingInSpecialty(item)
    );
    setMissingData(hasMissingData);
  };

  const toggleConfirmDelete = (displayId, value) => {
    setConfirmDeleteMap((prev) => ({
      ...prev,
      [displayId]: value,
    }));
  };

  const deleteAPiCall = async (deleteId) => {
    const specialtyToDelete = processedSpecialties.find(
      (specialityItem) => specialityItem.displayId === deleteId
    );

    if (specialtyToDelete) {
      try {
        await dispatch(
          fetchDeleteSpeciality({
            providerId: prvId,
            displayId: deleteId,
            specialtyData: specialtyToDelete,
          })
        );

        setConfirmDeleteMap((prev) => ({
          ...prev,
          [deleteId]: false,
        }));

        setProcessedSpecialties((prevSpecialties) =>
          prevSpecialties.filter((item) => item.displayId !== deleteId)
        );
        setAddedSpecialityValues((prevValues) =>
          prevValues.filter((item) => item.displayId !== deleteId)
        );
      } catch (error) {
        return error;
      }
    }
  };

  const saveNewSpecialties = async (newSpecialties) => {
    if (newSpecialties.length === 0) {
      return;
    }

    const addResponse = await dispatch(
      addProviderSpeciality({
        providerId: prvId,
        specialties: newSpecialties.map(toApiSpecialty),
      })
    );

    const addedDisplayId =
      addResponse?.payload?.data?.providerSpecialtiesResponseDTO?.[0]?.displayId;
    if (addedDisplayId) {
      setLastUpdatedId(addedDisplayId);
    }
  };

  const handleUpdateValue = (val) => {
    const updatedDisplayedId = normalizeSpecialty({
      ...val,
      displayId: editId.displayId,
      isValid: "true",
      isSelected: true,
    });
    setLastUpdatedId(editId.displayId);

    dispatch(
      updateProviderSpeciality({
        providerId: prvId,
        specialtyId: editId.displayId,
        specialtyData: {
          specialtyName: updatedDisplayedId.specialtyName,
          displayId: updatedDisplayedId.displayId,
          year: parseInt(updatedDisplayedId.year, 10) || 0,
          isValid: "true",
          isSelected: true,
          instituteName: getInstitutionName(updatedDisplayedId),
        },
      })
    ).then(() => {
      dispatch(fetchSpecialityList(prvId));
    });

    setOpenPopup(!openPopup);
    setEditId(null);
  };

  const RenderFormCreatorForAddSpeciality = () => {
    return (
      <div className="specialityFormWrapper">
        <CgFormCreator
          primaryButton={popupState === "edit" ? "Save" : "ADD"}
          noCancel={popupState === "edit" ? false : true}
          resetFormValues={resetValues}
          enablePrimaryBtn={popupState === "edit" ? true : enableAddButton}
          onSubmitCallback={(val) => {
            const dataExist = addedSpecialityValues.some(
              (item) => item.specialtyName === val.specialtyName
            );

            if (popupState !== "edit" && dataExist) {
              setRenderErrorMsg("This Speciality already exists");
            } else {
              setRenderErrorMsg("");
              if (popupState === "add") {
                const addedValue = normalizeSpecialty({
                  ...val,
                  displayId: Date.now().toString(20),
                  isValid: "true",
                });
                setAddedSpecialityValues([...addedSpecialityValues, addedValue]);
                setEnableAddButton(false);
                setEnableTrainingInstitution(false);
                setSelectedSpecialty("");
                setSelectedInstitution("");
              } else {
                handleUpdateValue(val);
                closePopupCallback();
              }
            }
          }}
          schema={AddSpecialitySchema}
          onCancel={() => {
            setOpenPopup(false);
          }}
          showErrorMsg={renderErrorMsg}
        />
        {popupState === "add" && (
          <AddedValuesList
            key={addedSpecialityValues}
            onClose={() => {
              setOpenPopup(false);
              setResetValues(true);
              closePopupCallback();
            }}
            onSave={async (res) => {
              try {
                if (res.length === 0) {
                  const allSpecialties = processedSpecialties || [];
                  for (const specialty of allSpecialties) {
                    await dispatch(
                      fetchDeleteSpeciality({
                        providerId: prvId,
                        displayId: specialty.displayId,
                        specialtyData: specialty,
                      })
                    );
                  }
                  closePopupCallback();
                  setProcessedSpecialties([]);
                  setAddedSpecialityValues([]);
                  await dispatch(fetchSpecialityList(prvId));
                  setOpenPopup(!openPopup);
                  return;
                }

                const currentSpecialties = processedSpecialties || [];
                const resultIds = res.map((item) => item.id);
                const removedSpecialties = currentSpecialties.filter(
                  (specialty) => !resultIds.includes(specialty.displayId)
                );
                for (const specialty of removedSpecialties) {
                  await dispatch(
                    fetchDeleteSpeciality({
                      providerId: prvId,
                      displayId: specialty.displayId,
                      specialtyData: specialty,
                    })
                  );
                }
                closePopupCallback();

                const result =
                  res?.map((item) => {
                    return item.metadata;
                  }) || [];

                const newSpecialties = result.filter(
                  (item) =>
                    !item.displayId ||
                    !currentSpecialties.some(
                      (s) => s.displayId === item.displayId
                    )
                );

                await saveNewSpecialties(newSpecialties);
                await dispatch(fetchSpecialityList(prvId));
                setOpenPopup(!openPopup);
              } catch (error) {
                setRenderErrorMsg(error);
              }
              closePopupCallback();
            }}
            enableSave
            renderAddedValues={true}
            addedValuesHeading="Your Specialties"
            primaryButton="SAVE"
            secondaryButton="CANCEL"
            addedValuesList={addedSpecialityValues
              ?.filter(
                (item) =>
                  item.isValid === "true" ||
                  item.isValid === true ||
                  item.isValid === "True"
              )
              .map((item) => {
                const institutionName = getInstitutionName(item);
                return {
                  name: `${item.specialtyName}${
                    institutionName ? ` - ${institutionName}` : ""
                  }${item.year ? ` - ${item.year}` : ""}`,
                  id: item.displayId,
                  metadata: item,
                };
              })}
          />
        )}
      </div>
    );
  };

  const RenderSpecialityError = () => {
    if (!showError) return null;
    let errorMessage = "The requested information could not be found.";
    if (addSpecialityError) {
      errorMessage = addSpecialityErrorMsg || errorMessage;
    } else if (updateSpecialityError) {
      errorMessage = updateSpecialityErrorMsg || errorMessage;
    } else if (deleteSpecialityError) {
      errorMessage = deleteSpecialityErrorMsg || errorMessage;
    } else if (directDeleteError) {
      errorMessage = directDeleteErrorMsg || errorMessage;
    } else if (specialityError) {
      errorMessage = specialityErrorMsg || errorMessage;
    }

    return (
      <div className="overlay-alert alert alert-danger" role="alert">
        {errorMessage}
      </div>
    );
  };

  const RenderSpecialityPopupModal = () => {
    if (!openPopup) {
      return null;
    }
    return (
      <PopupModal
        width="83%"
        customRender={RenderFormCreatorForAddSpeciality}
        isOpen={openPopup}
        onClose={() => {
          setOpenPopup(false);
          closePopupCallback();
        }}
        onSave={async () => {
          if (popupState !== "edit") {
            try {
              if (addedSpecialityValues.length === 0) {
                const allSpecialties = processedSpecialties || [];
                for (const specialty of allSpecialties) {
                  await dispatch(
                    fetchDeleteSpeciality({
                      providerId: prvId,
                      displayId: specialty.displayId,
                      specialtyData: specialty,
                    })
                  );
                }

                setProcessedSpecialties([]);
                setAddedSpecialityValues([]);
                await dispatch(fetchSpecialityList(prvId));
                setOpenPopup(!openPopup);
                closePopupCallback();
                return;
              }

              const currentSpecialties = processedSpecialties || [];
              const addedIds = addedSpecialityValues.map(
                (item) => item.displayId
              );
              const removedSpecialties = currentSpecialties.filter(
                (specialty) => !addedIds.includes(specialty.displayId)
              );

              for (const specialty of removedSpecialties) {
                await dispatch(
                  fetchDeleteSpeciality({
                    providerId: prvId,
                    displayId: specialty.displayId,
                    specialtyData: specialty,
                  })
                );
              }

              const newSpecialties = addedSpecialityValues.filter(
                (item) =>
                  !item.displayId ||
                  !currentSpecialties.some(
                    (s) => s.displayId === item.displayId
                  )
              );

              await saveNewSpecialties(newSpecialties);
              await dispatch(fetchSpecialityList(prvId));
              setOpenPopup(!openPopup);
            } catch (error) {
              setRenderErrorMsg(error);
            }
          }
          closePopupCallback();
        }}
        popupHeading={
          popupState === "edit" ? "Edit Specialty" : "Add Specialty"
        }
      />
    );
  };

  const RenderSpecialityDetails = () => {
    if (isSpecialityLoading) {
      return <div>Loading specialties...</div>;
    }

    if (specialityError) {
      return (
        <div className="error-message">
          <p>Error loading specialties: {specialityErrorMsg}</p>
          <button
            className="btn btn-primary"
            onClick={() => dispatch(fetchSpecialityList(prvId))}
          >
            Retry
          </button>
        </div>
      );
    }

    return (
      <>
        <div className="specialityOverviewHeader">
          <h3 className="section-header top-thirty overview">
            Specialties
            <a
              className="btn edit-button"
              onClick={() => {
                setOpenPopup(!openPopup);
                setPopupState("add");
                setResetValues(false);
                setEnableAddButton(false);
                setEnableTrainingInstitution(false);
                setSelectedSpecialty("");
                setSelectedInstitution("");
              }}
            >
              +Add
            </a>
          </h3>
          {missingData && (
            <div className="missing-data-info">
              <div>
                <img
                  className="missing-data-img"
                  src={redAlert}
                  alt="Missing Info Image"
                />
              </div>
              <div className="missing-data">
                <p> MISSING DATA</p>
              </div>
            </div>
          )}
        </div>
        <div className="specialitiesDataCard">
          {processedSpecialties.length > 0 ? (
            processedSpecialties.map((item, index) => {
              const institutionName = getInstitutionName(item);

              return (
                <div
                  className={`row specialityCardRow ${
                    item.displayId === lastUpdatedId ? "highlight-row" : ""
                  }`}
                  key={index}
                >
                  <div className="col-12 border specialityRowHeight">
                    <button
                      type="button"
                      className="btn border-0 editspeciality"
                      onClick={() => {
                        setOpenPopup(!openPopup);
                        setPopupState("edit");
                        setEditId(item);
                      }}
                    >
                      EDIT
                    </button>
                    |
                    {confirmDeleteMap[item.displayId] && (
                      <button
                        type="button"
                        className="btn border-0 canceldelete"
                        onClick={() =>
                          toggleConfirmDelete(item.displayId, false)
                        }
                      >
                        CANCEL
                      </button>
                    )}
                    <button
                      type="button"
                      className={`btn deleteBtn ${
                        confirmDeleteMap[item.displayId]
                          ? "deleteConfirmation"
                          : "border-0"
                      }`}
                      onClick={() => {
                        if (confirmDeleteMap[item.displayId]) {
                          deleteAPiCall(item.displayId);
                        } else {
                          toggleConfirmDelete(item.displayId, true);
                        }
                      }}
                    >
                      {confirmDeleteMap[item.displayId] ? "CONFIRM" : "DELETE"}
                    </button>
                  </div>
                  <div className="col-4 border">
                    <div className="table-content">
                      <p className="header">Specialty</p>
                      <p className="headerValue">{item.specialtyName}</p>
                    </div>
                  </div>
                  <div className="col-4 border">
                    <div className="table-content">
                      <p
                        className={`header ${
                          isInstituteMissingInSpecialty(item)
                            ? "error-label"
                            : ""
                        }`}
                      >
                        {isInstituteMissingInSpecialty(item)
                          ? "Missing Data"
                          : "Training Institution"}
                      </p>

                      {institutionName ||
                      item.specialtyName === GENERAL_DENTISTRY_LABEL ? (
                        <p className="headerValue">
                          {institutionName || "N/A"}
                        </p>
                      ) : (
                        <a
                          className="update-info-link"
                          onClick={() => {
                            setEditId(item);
                            setOpenPopup(!openPopup);
                            setPopupState("edit");
                          }}
                        >
                          Add Institution
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="col-4 border">
                    <div className="table-content">
                      <p className="header">Training Completed</p>
                      <p className="headerValue">{item.year}</p>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="empty-state">
              <p>{specialityErrorMsg || ""}</p>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div>
      {!onlyPopup && RenderSpecialityDetails()}
      {RenderSpecialityError()}
      {RenderSpecialityPopupModal()}
    </div>
  );
};

export default ProviderSpeciality;
